import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import IconButton from '@mui/material/IconButton'
import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { hasUnrestrictedDataAccess, useUser } from 'hooks/user'
import pageFrame from 'styles/pageFrame.module.css'

import style from './Account.module.css'

const SIDEBAR_COLLAPSED_KEY = 'fms:account-sidebar-collapsed'

const Account = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const user = useUser()
  const canUseIam = Boolean(user?.state.canUseIam)
  const canManageOrganizations = Boolean(
    user && (hasUnrestrictedDataAccess(user.state) || user.state.audience === 'reseller')
  )
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed])

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev)
  }, [])

  if (location.pathname === PATH.ACCOUNT || location.pathname === `${PATH.ACCOUNT}/`) {
    return <Navigate to={PATH.ACCOUNT_NOTIFICATIONS} replace />
  }

  const teamEditorOpen =
    location.pathname === PATH.ACCOUNT_USERS && new URLSearchParams(location.search).has('user')
  const mainFill =
    (location.pathname === PATH.ACCOUNT_USERS && !teamEditorOpen) ||
    location.pathname === PATH.ACCOUNT_NOTIFICATIONS ||
    location.pathname === PATH.ACCOUNT_ORGANIZATIONS

  return (
    <div
      className={clsx(pageFrame.frame, style.page, sidebarCollapsed && style.pageSidebarCollapsed)}
      data-test-id={getTestId('account')}
    >
      <aside
        className={clsx(style.sidebar, sidebarCollapsed && style.sidebarCollapsed)}
        data-test-id={getTestId('account-sidebar')}
      >
        <div className={style.sidebarHeader}>
          {!sidebarCollapsed ? <div className={style.sidebarTitle}>{t('account.title')}</div> : null}
          <IconButton
            size="small"
            onClick={toggleSidebar}
            className={style.sidebarToggle}
            aria-label={sidebarCollapsed ? t('account.expand_sidebar') : t('account.collapse_sidebar')}
            data-test-id={getTestId('account-sidebar-toggle')}
          >
            {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </div>
        {!sidebarCollapsed ? (
          <>
            <NavLink
              to={PATH.ACCOUNT_PROFILE}
              className={({ isActive }) => clsx(style.navButton, isActive && style.navButtonActive)}
              data-test-id={getTestId('account-nav-profile')}
            >
              {t('account.nav_profile')}
            </NavLink>
            {canUseIam ? (
              <NavLink
                to={PATH.ACCOUNT_USERS}
                className={({ isActive }) => clsx(style.navButton, isActive && style.navButtonActive)}
                data-test-id={getTestId('account-nav-users')}
              >
                {t('account.nav_users')}
              </NavLink>
            ) : null}
            {canManageOrganizations ? (
              <NavLink
                to={PATH.ACCOUNT_ORGANIZATIONS}
                className={({ isActive }) => clsx(style.navButton, isActive && style.navButtonActive)}
                data-test-id={getTestId('account-nav-organizations')}
              >
                {t('account.nav_organizations')}
              </NavLink>
            ) : null}
            <NavLink
              to={PATH.ACCOUNT_NOTIFICATIONS}
              className={({ isActive }) => clsx(style.navButton, isActive && style.navButtonActive)}
              data-test-id={getTestId('account-nav-history')}
            >
              {t('account.nav_history')}
            </NavLink>
            <NavLink
              to={PATH.ACCOUNT_NOTIFICATION_SETTINGS}
              className={({ isActive }) => clsx(style.navButton, isActive && style.navButtonActive)}
              data-test-id={getTestId('account-nav-notification-settings')}
            >
              {t('account.nav_notification_settings')}
            </NavLink>
            <NavLink
              to={PATH.ACCOUNT_FAVORITES}
              className={({ isActive }) => clsx(style.navButton, isActive && style.navButtonActive)}
              data-test-id={getTestId('account-nav-favorites')}
            >
              {t('account.nav_favorites')}
            </NavLink>
          </>
        ) : null}
      </aside>
      <main className={clsx(style.main, mainFill && style.mainFill)}>
        <Outlet />
      </main>
    </div>
  )
}

export default Account
